import React from 'react';

/**
 * Reusable loading spinner component
 * Requirements: 12.2
 * 
 * @param {Object} props - Component props
 * @param {string} props.size - Size of spinner: 'small', 'medium', 'large' (default: 'medium')
 * @param {string} props.message - Optional loading message to display
 * @param {boolean} props.fullPage - Whether to center in full page (default: false)
 * @param {string} props.color - Color of the spinner (default: '#3498db')
 */
const LoadingSpinner = ({ 
  size = 'medium',
  message = '',
  fullPage = false,
  color = '#3498db'
}) => {
  const sizeMap = {
    small: 24,
    medium: 40,
    large: 60
  };

  const spinnerSize = sizeMap[size] || sizeMap.medium;
  const borderWidth = size === 'small' ? 3 : size === 'large' ? 5 : 4;

  const containerStyle = fullPage 
    ? styles.fullPageContainer 
    : styles.container;

  return (
    <div style={containerStyle}>
      <div 
        style={{
          ...styles.spinner,
          width: `${spinnerSize}px`,
          height: `${spinnerSize}px`,
          border: `${borderWidth}px solid #ecf0f1`,
          borderTop: `${borderWidth}px solid ${color}`,
        }}
      />
      {message && (
        <p style={{
          ...styles.message,
          fontSize: size === 'small' ? '12px' : size === 'large' ? '18px' : '14px'
        }}>
          {message}
        </p>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  },
  fullPageContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    width: '100%'
  },
  spinner: {
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  message: {
    marginTop: '16px',
    color: '#7f8c8d',
    fontWeight: '500',
    textAlign: 'center'
  }
};

// Add keyframe animation for spinner
if (typeof document !== 'undefined') {
  const styleSheet = document.styleSheets[0];
  const keyframes = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  try {
    styleSheet.insertRule(keyframes, styleSheet.cssRules.length);
  } catch (e) {
    // Ignore if already exists
  }
}

export default LoadingSpinner;
